// GlobalOverlays — global overlay/modal/toast layer rendered on top of every view.
//
// Extracted VERBATIM from the former monolith (legacy/App.jsx):
//   1. Global player-profile modal (PlayerHistoryModal)
//   2. Activity court-reservation modal (IIFE rendering SerieBookingPanel)
//   3. Global "Report a bug" floating button (BugReportButton)
//   4. Toast messages
//   5. Series-edit scope picker modal (seriesEditPending)
//   6. Series-edit confirm modal (seriesEditConfirm + seriesEditPending IIFE)
//
// Presentational only: every free variable from the former monolith is now a prop.
// getPlayerTournamentHistory / calcPlayerPositionInTournament are imported
// directly (they were module-level helpers in the former monolith, not free vars).

import { useId, useRef, type CSSProperties } from 'react';
import { Check, AlertCircle, X } from 'lucide-react';
import { useT } from '../i18n';
import type { Toast } from '../state/toast';
import { Dialog } from '../components/ui';
import { safeId } from '../components/ui/labelledControl';
import { PlayerHistoryModal } from '../components/players';
import { SerieBookingPanel } from '../components/seriespill';
import { DevMenuButton } from '../components/dev';
import { MessagesDock } from '../components/messages';
import type { MessagingApi } from '../state/useMessaging';
import { getPlayerTournamentHistory } from '../engine/playerDb';
import { calcPlayerPositionInTournament } from '../engine/padel24Results';

export interface GlobalOverlaysProps {
  profilePlayerId: any;
  setProfilePlayerId: any;
  effectivePlayerDB: any;
  archiveWithLive: any;
  singleMatches: any;
  eloAdjustments: any;
  view: any;
  openArchived: any;
  setProfileReturn: any;
  Padel24Engine: any;
  courtReservation: any;
  reservationDraft: any;
  setReservationDraft: any;
  activities: any;
  closeActivityReservation: any;
  syncBookingsFromPanel: any;
  tournament: any;
  archive: any;
  allPlayers: any;
  setView: any;
  toast: Toast | null;
  setToast: any;
  seriesEditPending: any;
  setSeriesEditPending: any;
  seriesEditConfirm: any;
  setSeriesEditConfirm: any;
  applySeriesEdit: any;
  /** Hide the floating bug-report button (standalone member app). */
  hideBugButton?: boolean;
  /**
   * The persona switcher's hooks, from the auth gate that owns the session.
   * Present turns the floating bug button into the DEV menu — but only where
   * the API actually offers personas, which DevMenuButton checks for itself.
   */
  devPersona?: import('../components/dev').DevMenuButtonProps['devPersona'];
  /** The club's messaging slice — renders the Meldinger dock when given. */
  messaging?: MessagingApi;
  clubInfo?: any;
  /** Opens the full-screen «Meldinger» view. */
  onOpenMessagesView?: () => void;
  /** The dock's open state (toggled from the header icon). */
  dockOpen?: boolean;
  onToggleDock?: () => void;
}

/**
 * Visually hidden, but present in the accessibility tree.
 *
 * NOT `width: 0; height: 0`: `overflow` defaults to `visible`, so text in a
 * zero-sized box still paints — which is what the first version of the live
 * region below did, putting a second copy of every toast in the corner of the
 * viewport. The clip-plus-overflow form is the one that actually hides it.
 */
export const SR_ONLY: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
  border: 0,
};

export function GlobalOverlays(props: GlobalOverlaysProps) {
  const t = useT();
  const uid = safeId('global-overlays', useId());
  const ids = {
    reserveKicker: `${uid}-reserve-kicker`,
    reserveName: `${uid}-reserve-name`,
    scopeTitle: `${uid}-scope-title`,
    scopeLead: `${uid}-scope-lead`,
    confirmTitle: `${uid}-confirm-title`,
    confirmBody: `${uid}-confirm-body`,
  };
  // The series confirmation starts on «Tilbake», the choice that changes
  // nothing — the same rule every other confirmation in #900 follows.
  const confirmBackRef = useRef<HTMLButtonElement | null>(null);
  const {
    profilePlayerId,
    setProfilePlayerId,
    effectivePlayerDB,
    archiveWithLive,
    singleMatches,
    eloAdjustments,
    view,
    openArchived,
    setProfileReturn,
    Padel24Engine,
    courtReservation,
    reservationDraft,
    setReservationDraft,
    activities,
    closeActivityReservation,
    syncBookingsFromPanel,
    tournament,
    archive,
    allPlayers,
    setView,
    toast,
    setToast,
    seriesEditPending,
    setSeriesEditPending,
    seriesEditConfirm,
    setSeriesEditConfirm,
    applySeriesEdit,
    hideBugButton = false,
    devPersona,
    messaging,
    clubInfo,
    onOpenMessagesView,
    dockOpen = false,
    onToggleDock,
  } = props;
  // A toast from the state layer carries a key, not a sentence (state/toast.ts).
  const toastText = toast
    ? toast.messageKey
      ? t(toast.messageKey, toast.messageVars)
      : toast.message
    : '';
  const toastAction = toast?.action;

  return (
    <>
      {/* Global player profile — shown from any view */}
      {profilePlayerId && (
        <PlayerHistoryModal
          playerId={profilePlayerId}
          effectivePlayerDB={effectivePlayerDB}
          archive={archiveWithLive}
          singleMatches={singleMatches}
          eloAdjustments={eloAdjustments}
          onClose={() => setProfilePlayerId(null)}
          onOpenArchived={(t) => {
            const ret = { view, playerId: profilePlayerId };
            setProfilePlayerId(null);
            openArchived(t);
            setProfileReturn(ret);
          }}
          Padel24Engine={Padel24Engine}
          getPlayerTournamentHistory={getPlayerTournamentHistory}
          calcPlayerPositionInTournament={calcPlayerPositionInTournament}
        />
      )}

      {/* ACTIVITIES: reserve courts in the SHARED booking panel (same as
          seriespill). The tournament is passed in via a series adapter; no classes
          and no match placement (onPlaceMatches=null). */}
      {courtReservation &&
        reservationDraft &&
        (() => {
          const act = activities.find((a) => a.id === courtReservation);
          if (!act) return null;
          // The reserve flow requires seasonStart + deadline (season cutoff). A tournament
          // has no season, so we give it a valid window: start = the activity's date
          // (or today), deadline = +1 year (enough for a fixed date; recurrence is governed
          // by the time range's own "ends" date).
          const _today = (() => {
            const d = new Date();
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          })();
          const _startISO = act.config?.activityDate || _today;
          const _end = new Date(`${_startISO}T00:00:00`);
          _end.setFullYear(_end.getFullYear() + 1);
          const _endISO = `${_end.getFullYear()}-${String(_end.getMonth() + 1).padStart(2, '0')}-${String(_end.getDate()).padStart(2, '0')}`;
          const seriesAdapter = {
            id: act.id,
            matches: [],
            config: {
              name: act.config?.name || 'Aktivitet',
              classes: [],
              seasonStart: _startISO,
              deadline: _endISO,
              courtMode: 'unallocated',
              slotMode: reservationDraft.slotMode || 'integrated',
            },
          };
          return (
            // A `Dialog` (#900), named by its kicker and the activity's name.
            // It never closed on a backdrop click and still does not; Escape
            // is «Ferdig», the one way out it had. The booking panel's own
            // dialogs open on top of it and own the keyboard while they do.
            <Dialog
              labelledBy={`${ids.reserveKicker} ${ids.reserveName}`}
              onClose={closeActivityReservation}
              showClose={false}
              closeOnBackdropClick={false}
              backdropClassName=""
              backdropStyle={{
                position: 'fixed',
                inset: 0,
                zIndex: 9000,
                background: 'rgba(0,0,0,0.5)',
              }}
              className=""
              style={{
                background: '#ffffff',
                maxWidth: 1100,
                width: '95%',
                margin: '2vh auto',
                borderRadius: 16,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                maxHeight: '96vh',
              }}
            >
              <div
                className="flex items-center justify-between gap-3 px-5 py-3"
                style={{ borderBottom: '1px solid #e5e7eb' }}
              >
                <div className="min-w-0">
                  <div
                    id={ids.reserveKicker}
                    className="font-mono text-[10px] uppercase tracking-wider text-ink-3"
                  >
                    Reserver baner i Padelsenter-kalenderen
                  </div>
                  <div
                    id={ids.reserveName}
                    className="font-display text-base font-semibold text-ink-1 truncate"
                  >
                    {act.config?.name || 'Aktivitet'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={closeActivityReservation}
                  className="btn-lime text-sm flex-shrink-0"
                >
                  <Check size={14} /> Ferdig
                </button>
              </div>
              <div style={{ overflowY: 'auto', flex: 1 }} className="p-4">
                <SerieBookingPanel
                  series={seriesAdapter}
                  draft={reservationDraft}
                  setDraft={setReservationDraft}
                  onPlaceMatches={null}
                  onBookingsChanged={syncBookingsFromPanel}
                />
              </div>
            </Dialog>
          );
        })()}

      {/* Meldinger dock — drops down under the header's Meldinger icon. Hidden in
          the standalone member app, which has its own Meldinger tab. */}
      {messaging && !hideBugButton && (
        <MessagesDock
          messaging={messaging}
          effectivePlayerDB={effectivePlayerDB || []}
          clubInfo={clubInfo}
          open={dockOpen && view !== 'messages'}
          onToggle={() => onToggleDock?.()}
          onFullscreen={() => {
            if (dockOpen) onToggleDock?.();
            onOpenMessagesView?.();
          }}
        />
      )}

      {/* The single floating button — every page, except the standalone member
          app (native shell / ?native=1): it would sit on top of the tab bar.
          It is the bug button, and the DEV menu where the API offers personas;
          `DevMenuButton` decides which, so there is never more than one. */}
      {!hideBugButton && (
        <DevMenuButton
          devPersona={devPersona}
          currentView={view}
          onOpenArchive={() => setView('bugs')}
          currentContext={{
            hasActiveTournament: !!tournament,
            tournamentType: tournament?.type || null,
            tournamentStatus: tournament?.status || null,
            archiveCount: archive?.length || 0,
            singleMatchesCount: singleMatches?.length || 0,
            allPlayersCount: allPlayers?.length || 0,
          }}
        />
      )}

      {/* Toast messages */}
      {/* ── THE SAVE-SUCCESS/FAILURE CHANNEL, NOW AUDIBLE (#781) ──────
          `showToast()` has 142 call sites and is how the whole app reports
          that a save worked or did not. The element had no `role` and no
          `aria-live`, so every one of those outcomes was invisible to
          assistive technology: a failed save said nothing at all.

          ── TWO REGIONS, EACH WITH A FIXED ROLE (#812 review) ──────────

          The first version of this was ONE region whose role flipped with the
          toast — `alert`/assertive while empty, `status`/polite the moment a
          success arrived. That is not the stable region it claimed to be: the
          role changed at the same instant as the text, which is the dynamic
          -region behaviour the whole design was meant to avoid. Several
          screen-reader/browser pairs handle that badly, and the one thing we
          know is that it is not predictable.

          So there are two, both mounted for the life of the app, both with a
          role that never changes, and the message is written into whichever
          one matches. A failure interrupts (`alert`/assertive); a
          confirmation waits its turn (`status`/polite). One role for both
          would make every «lagret» cut the user off mid-sentence, which is
          how live regions get switched off by the people they exist for.

          AND THEY ARE ACTUALLY HIDDEN. The first version used a 0×0 fixed
          box, which does NOT hide text — `overflow` defaults to `visible`, so
          the message painted outside the box at the corner of the viewport,
          a second copy next to the real toast. `clip` plus `overflow: hidden`
          on a 1px box is the technique that genuinely hides it while leaving
          it in the accessibility tree. */}
      <div
        data-testid="toast-live-polite"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={SR_ONLY}
      >
        {toast?.type === 'success' ? toastText : ''}
      </div>
      <div
        data-testid="toast-live-assertive"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        style={SR_ONLY}
      >
        {toast && toast.type !== 'success' ? toastText : ''}
      </div>
      {toast && (
        <div
          style={{
            position: 'fixed',
            // Above the «new version» banner while it shows (`NEW_BUILD_BANNER_HEIGHT_VAR`).
            bottom: 'calc(24px + var(--new-build-banner-h, 0px))',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10000,
            maxWidth: 480,
            padding: '12px 18px',
            borderRadius: 12,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            background: toast.type === 'success' ? '#dcfce7' : '#fee2e2',
            color: toast.type === 'success' ? '#166534' : '#9f1239',
            border: `1px solid ${toast.type === 'success' ? '#86efac' : '#fca5a5'}`,
            boxShadow: '0 10px 30px -5px rgba(0,0,0,0.2)',
            fontSize: 14,
            fontWeight: 500,
            lineHeight: 1.4,
          }}
        >
          {/* HIDDEN FROM ASSISTIVE TECH, because the live region above
              already announces this exact text — exposing both makes a
              screen-reader user meet the same message twice. The dismiss
              button is deliberately NOT inside this, so it stays reachable. */}
          <span aria-hidden="true" style={{ display: 'contents' }}>
            {toast.type === 'success' ? (
              <Check size={16} style={{ flexShrink: 0, marginTop: 2 }} />
            ) : (
              <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
            )}
            <span>{toastText}</span>
          </span>
          {/* THE TOAST'S ONE ACTION (#1305), outside the aria-hidden span so a
              keyboard or screen-reader user can reach it. The assertive region
              announces the message; the button carries its own name. */}
          {toastAction && (
            <button
              type="button"
              onClick={() => toastAction.run()}
              style={{
                background: 'transparent',
                border: '1px solid currentColor',
                borderRadius: 8,
                cursor: 'pointer',
                padding: '2px 10px',
                color: 'inherit',
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              {t(toastAction.labelKey)}
            </button>
          )}
          <button
            type="button"
            aria-label={t('ui.toast.dismiss')}
            onClick={() => setToast(null)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              margin: '-4px -4px -4px 4px',
              color: 'inherit',
              opacity: 0.7,
            }}
          >
            <X size={14} />
          </button>
        </div>
      )}
      {seriesEditPending && (
        // A `Dialog` (#900). Escape is «Avbryt», as the backdrop is.
        <Dialog
          labelledBy={ids.scopeTitle}
          describedBy={ids.scopeLead}
          onClose={() => setSeriesEditPending(null)}
          showClose={false}
          className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl overflow-y-auto max-h-[92vh]"
          style={{ maxHeight: 'calc(100dvh - 2rem)' }}
        >
          <h3 id={ids.scopeTitle} className="font-display text-lg font-semibold text-ink-1 mb-1">
            Lagre endringer i serien
          </h3>
          <p id={ids.scopeLead} className="text-sm text-ink-3 mb-4">
            Fordi dette er en gjentakende serie har du <strong>flere valg</strong> — hvor skal
            endringene gjelde?
          </p>
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => applySeriesEdit('this')}
              className="w-full text-left p-3 rounded-lg border-2 border-line hover:border-lime-deep transition-colors"
            >
              <div className="text-sm font-semibold text-ink-1">Bare denne</div>
              <div className="text-[12px] text-ink-3">
                Endringene gjelder kun denne ene aktiviteten.
              </div>
            </button>
            <button
              type="button"
              onClick={() => setSeriesEditConfirm('future')}
              className="w-full text-left p-3 rounded-lg border-2 border-line hover:border-lime-deep transition-colors"
            >
              <div className="text-sm font-semibold text-ink-1">Denne og fremtidige</div>
              <div className="text-[12px] text-ink-3">
                Denne og alle senere aktiviteter i serien.
              </div>
            </button>
            <button
              type="button"
              onClick={() => setSeriesEditConfirm('all')}
              className="w-full text-left p-3 rounded-lg border-2 border-line hover:border-lime-deep transition-colors"
            >
              <div className="text-sm font-semibold text-ink-1">Alle i serien</div>
              <div className="text-[12px] text-ink-3">Alle aktiviteter i hele serien.</div>
            </button>
          </div>
          <p className="text-[11px] text-ink-3 mt-3">
            Innstillinger (nivå, pris, maks, påmelding, sted) brer seg til valgt omfang. Hver
            aktivitet beholder sin egen dato og banereservasjon.
          </p>
          <button
            type="button"
            onClick={() => setSeriesEditPending(null)}
            className="mt-3 text-sm text-ink-3 hover:text-ink-1"
          >
            Avbryt
          </button>
        </Dialog>
      )}
      {seriesEditConfirm &&
        seriesEditPending &&
        (() => {
          const pend = seriesEditPending;
          const base = (activities || []).find((a) => a.id === pend.activity.id) || pend.activity;
          const dateOf = (a) => (a.bookingDraft?.slotRooms || [])[0]?.singleDate || '';
          const myDate = dateOf(base);
          const occs = (activities || []).filter(
            (a) =>
              a.recurringId === base.recurringId &&
              (seriesEditConfirm === 'all' ? true : dateOf(a) >= myDate),
          );
          const scopeLabel = seriesEditConfirm === 'future' ? 'denne og alle fremtidige' : 'alle';
          return (
            // A `Dialog` (#900), opened ON TOP of the scope picker, which stays
            // mounted under it: this one is the innermost, so Escape and its
            // backdrop step back to the picker (as «Tilbake» does) rather than
            // closing both, and focus returns to the scope that was chosen.
            <Dialog
              labelledBy={ids.confirmTitle}
              describedBy={ids.confirmBody}
              onClose={() => setSeriesEditConfirm(null)}
              showClose={false}
              initialFocus={confirmBackRef}
              backdropClassName="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-ink/40 backdrop-blur-sm"
              className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl overflow-y-auto max-h-[92vh]"
              style={{ maxHeight: 'calc(100dvh - 2rem)' }}
            >
              <h3
                id={ids.confirmTitle}
                className="font-display text-lg font-semibold text-ink-1 mb-2"
              >
                Bekreft endring i serien
              </h3>
              <p id={ids.confirmBody} className="text-sm text-ink-2 mb-4">
                Endringene blir brukt på <strong>{scopeLabel}</strong> — {occs.length} aktivitet
                {occs.length === 1 ? '' : 'er'} i serien. Hver enkelt beholder sin egen dato og
                banereservasjon.
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const s = seriesEditConfirm;
                    setSeriesEditConfirm(null);
                    applySeriesEdit(s);
                  }}
                  className="text-sm font-semibold px-4 py-2 rounded-lg text-white hover:opacity-90"
                  style={{ background: 'var(--lime-deep)' }}
                >
                  Ja, bruk på {occs.length}
                </button>
                <button
                  ref={confirmBackRef}
                  type="button"
                  onClick={() => setSeriesEditConfirm(null)}
                  className="text-sm px-3 py-2 rounded-lg text-ink-3"
                >
                  Tilbake
                </button>
              </div>
            </Dialog>
          );
        })()}
    </>
  );
}
